import { ServiceLogo } from "@/components/ServiceLogo";
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Wrench, Droplets, Zap, Thermometer, Home, Hammer, Paintbrush, Blocks, DoorOpen,
  Key, Sprout, Sparkles, Truck, HeartHandshake, MoreHorizontal, ArrowLeft, ArrowRight,
  CheckCircle2, ShieldCheck, MapPin, Clock, Camera, Loader2,
} from "lucide-react";

const ICONS = { Wrench, Droplets, Zap, Thermometer, Home, Hammer, Paintbrush, Blocks, DoorOpen, Key, Sprout, Sparkles, Truck, HeartHandshake, MoreHorizontal };
const URGENCIES = [
  { v: "urgent", label: "Urgent", hint: "sous 24-48 h" },
  { v: "semaine", label: "Dans la semaine", hint: "" },
  { v: "flexible", label: "Je ne suis pas pressé(e)", hint: "" },
];
const HOUSING = ["Maison", "Appartement", "Local commercial", "Autre"];
const TOTAL_STEPS = 5;

const inputCls = "w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-[#E05638] focus:ring-2 focus:ring-orange-100 outline-none text-sm";
const Field = ({ label, children }) => (
  <label className="block"><span className="block text-sm font-semibold text-[#172A3A] mb-1.5">{label}</span>{children}</label>
);

export default function QuoteRequest() {
  const { tradeSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const targetPro = searchParams.get("pro") || "";
  const [trades, setTrades] = useState([]);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    trade_slug: "", subneed: "", requester_type: "", city: "", postal_code: "", housing_type: "",
    address: "", description: "", urgency: "flexible", budget: "", desired_date: "",
    photos: [], first_name: "", last_name: "", phone: "", email: "", consent: false,
  });

  useSeo({
    title: "Demandez vos devis locaux gratuits — Chartres & Eure-et-Loir | PROXIreduc-28",
    description: "Décrivez votre besoin, nous trouvons les professionnels adaptés près de chez vous. Devis gratuits et sans engagement à Chartres et en Eure-et-Loir.",
    canonicalPath: tradeSlug ? `/demande-de-devis/${tradeSlug}` : "/demande-de-devis",
  });

  useEffect(() => {
    api.get("/devis/trades").then((r) => setTrades(r.data.trades || [])).catch(() => {});
  }, []);
  // Pré-sélection : un pro ou une organisation (asso/club) qui lance une demande est par défaut « Professionnel ».
  useEffect(() => {
    if (user && (user.role === "professional" || user.role === "organization")) {
      setForm((f) => (f.requester_type ? f : { ...f, requester_type: "pro" }));
    }
  }, [user]);
  useEffect(() => {
    if (tradeSlug && trades.length) {
      const t = trades.find((x) => x.slug === tradeSlug);
      if (t) setForm((f) => ({ ...f, trade_slug: t.slug }));
    }
  }, [tradeSlug, trades]);

  const selectedTrade = useMemo(() => trades.find((t) => t.slug === form.trade_slug), [trades, form.trade_slug]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 1) return !!form.trade_slug;
    if (step === 2) return true;
    if (step === 3) return form.city.trim().length > 1;
    if (step === 4) return form.description.trim().length >= 10;
    return true;
  };

  const next = () => { if (canNext()) setStep((s) => Math.min(TOTAL_STEPS, s + 1)); };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/entre-voisins/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.url) urls.push(data.url);
      }
      set("photos", [...form.photos, ...urls].slice(0, 5));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.first_name.trim()) return toast.error("Indiquez votre prénom.");
    if (!form.phone.trim()) return toast.error("Indiquez votre téléphone.");
    if (!form.email.trim()) return toast.error("Indiquez votre e-mail.");
    if (!form.consent) return toast.error("Merci d'accepter la transmission de votre demande.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/devis/requests", { ...form, target_business_id: targetPro });
      toast.success("Demande envoyée !");
      navigate(data.track_url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F2F4F5]" data-testid="quote-request-page">
      {/* Bandeau */}
      <div className="bg-[#172A3A] text-white">
        <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white mb-4"><ArrowLeft size={15} /> Accueil</Link>
          <ServiceLogo serviceKey="demande-de-devis" size={76} className="mb-4" />
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">Demandez vos devis locaux</h1>
          <p className="mt-3 text-base text-white/80 max-w-xl">Décrivez votre besoin, nous trouvons les professionnels adaptés près de chez vous. <strong className="text-white">Gratuit et sans engagement.</strong></p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 -mt-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sm:p-8">
          {/* Progression */}
          <div className="flex items-center gap-2 mb-6" data-testid="quote-progress">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < step ? "bg-[#E05638]" : "bg-slate-200"}`} />
            ))}
          </div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Étape {step} / {TOTAL_STEPS}</div>

          {/* ÉTAPE 1 — Métier */}
          {step === 1 && (
            <div data-testid="quote-step-1">
              <h2 className="font-heading text-xl font-bold text-[#172A3A] mb-4">Quel est votre besoin ?</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {trades.map((t) => {
                  const Icon = ICONS[t.icon] || Wrench;
                  const active = form.trade_slug === t.slug;
                  return (
                    <button key={t.slug} type="button" onClick={() => { set("trade_slug", t.slug); set("subneed", ""); }}
                      data-testid={`quote-trade-${t.slug}`}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 text-center transition-all ${active ? "border-[#E05638] bg-orange-50" : "border-slate-150 bg-white hover:border-slate-300"}`}>
                      <Icon size={26} className={active ? "text-[#E05638]" : "text-[#66727A]"} />
                      <span className="text-xs font-semibold text-[#172A3A] leading-tight">{t.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ÉTAPE 2 — Sous-besoin */}
          {step === 2 && (
            <div data-testid="quote-step-2">
              <h2 className="font-heading text-xl font-bold text-[#172A3A] mb-4">Précisez votre {selectedTrade?.name?.toLowerCase()}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {(selectedTrade?.subneeds || []).map((s) => (
                  <button key={s} type="button" onClick={() => set("subneed", s)} data-testid={`quote-subneed-${s}`}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium text-left transition-all ${form.subneed === s ? "border-[#E05638] bg-orange-50 text-[#172A3A]" : "border-slate-150 hover:border-slate-300 text-slate-700"}`}>
                    <CheckCircle2 size={16} className={form.subneed === s ? "text-[#E05638]" : "text-slate-300"} /> {s}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-3">Facultatif — vous pourrez tout détailler à l'étape suivante.</p>
            </div>
          )}

          {/* ÉTAPE 3 — Localisation */}
          {step === 3 && (
            <div data-testid="quote-step-3" className="space-y-4">
              <h2 className="font-heading text-xl font-bold text-[#172A3A] flex items-center gap-2"><MapPin size={20} className="text-[#E05638]" /> Où se situe le projet ?</h2>
              <Field label="Vous êtes (facultatif)">
                <div className="flex flex-wrap gap-2">
                  {[{ v: "particulier", label: "Particulier" }, { v: "pro", label: "Professionnel" }].map((o) => (
                    <button key={o.v} type="button"
                      onClick={() => set("requester_type", form.requester_type === o.v ? "" : o.v)}
                      data-testid={`quote-requester-${o.v}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${form.requester_type === o.v ? "border-[#E05638] bg-orange-50 text-[#E05638]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{o.label}</button>
                  ))}
                </div>
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Commune *"><input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Chartres" data-testid="quote-city" /></Field>
                <Field label="Code postal"><input className={inputCls} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} placeholder="28000" data-testid="quote-postal" /></Field>
              </div>
              <Field label="Type de logement">
                <div className="flex flex-wrap gap-2">
                  {HOUSING.map((h) => (
                    <button key={h} type="button" onClick={() => set("housing_type", h)} data-testid={`quote-housing-${h}`}
                      className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${form.housing_type === h ? "border-[#E05638] bg-orange-50 text-[#E05638]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{h}</button>
                  ))}
                </div>
              </Field>
              <Field label="Adresse (facultatif)"><input className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="N° et rue" data-testid="quote-address" /></Field>
            </div>
          )}

          {/* ÉTAPE 4 — Description */}
          {step === 4 && (
            <div data-testid="quote-step-4" className="space-y-4">
              <h2 className="font-heading text-xl font-bold text-[#172A3A]">Décrivez votre projet</h2>
              <Field label="Description *">
                <textarea className={`${inputCls} h-28 py-2.5`} value={form.description} onChange={(e) => set("description", e.target.value)}
                  placeholder="Expliquez en quelques phrases ce dont vous avez besoin…" data-testid="quote-description" />
              </Field>
              <Field label="Niveau d'urgence">
                <div className="flex flex-wrap gap-2">
                  {URGENCIES.map((u) => (
                    <button key={u.v} type="button" onClick={() => set("urgency", u.v)} data-testid={`quote-urgency-${u.v}`}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${form.urgency === u.v ? "border-[#E05638] bg-orange-50 text-[#E05638]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      <Clock size={14} /> {u.label}{u.hint ? <span className="text-xs opacity-70">· {u.hint}</span> : null}
                    </button>
                  ))}
                </div>
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Budget indicatif (facultatif)"><input className={inputCls} value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder="ex. 100-300 €" data-testid="quote-budget" /></Field>
                <Field label="Date souhaitée (facultatif)"><input className={inputCls} value={form.desired_date} onChange={(e) => set("desired_date", e.target.value)} placeholder="ex. semaine prochaine" data-testid="quote-date" /></Field>
              </div>
              <Field label="Photos (facultatif)">
                <label className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-slate-200 text-sm text-slate-500 cursor-pointer hover:border-[#E05638]" data-testid="quote-photos">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />} Ajouter des photos
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} />
                </label>
                {form.photos.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {form.photos.map((u, i) => <img key={i} src={u} alt="" className="w-16 h-16 rounded-lg object-cover border" />)}
                  </div>
                )}
              </Field>
            </div>
          )}

          {/* ÉTAPE 5 — Coordonnées */}
          {step === 5 && (
            <div data-testid="quote-step-5" className="space-y-4">
              <h2 className="font-heading text-xl font-bold text-[#172A3A]">Vos coordonnées</h2>
              <p className="text-sm text-slate-500">Pour que les professionnels puissent vous recontacter. Aucune création de compte obligatoire.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Prénom *"><input className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="quote-firstname" /></Field>
                <Field label="Nom"><input className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="quote-lastname" /></Field>
                <Field label="Téléphone *"><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="06 12 34 56 78" data-testid="quote-phone" /></Field>
                <Field label="E-mail *"><input className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="vous@email.fr" data-testid="quote-email" /></Field>
              </div>
              <label className="flex items-start gap-2.5 p-3 rounded-xl bg-[#F2F4F5] cursor-pointer" data-testid="quote-consent">
                <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#E05638]" />
                <span className="text-xs text-slate-600 leading-relaxed">J'accepte que ma demande et mes coordonnées soient transmises aux professionnels locaux sélectionnés (jusqu'à 3) afin qu'ils m'adressent un devis. <ShieldCheck size={12} className="inline text-emerald-600" /></span>
              </label>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between gap-3 mt-7 pt-5 border-t border-slate-100">
            {step > 1 ? (
              <button onClick={prev} data-testid="quote-prev" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100"><ArrowLeft size={15} /> Retour</button>
            ) : <span />}
            {step < TOTAL_STEPS ? (
              <button onClick={next} disabled={!canNext()} data-testid="quote-next"
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-[#E05638] hover:bg-[#c7482d] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Continuer <ArrowRight size={15} />
              </button>
            ) : (
              <button onClick={submit} disabled={submitting || !form.consent} data-testid="quote-submit"
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-bold text-white bg-[#E05638] hover:bg-[#c7482d] disabled:opacity-50 transition-colors">
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Envoyer ma demande gratuite
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
